import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from 'react-query';
import {
  usePostManagementWithInfinityQuery,
  usePostManagementWithoutInfinityQuery,
} from '../../../../hooks/post/usePostManagement';
import {
  TPostInfinityQuery,
  TPostQuery,
} from '../../../../interfaces/post.interface';
import Post from './Post';
import PostSkeleton from './PostSkeleton';

interface IPostFeedWithInfinityQuery {
  queryKey: QueryKey;
  queryFn: QueryFunction<TPostInfinityQuery, QueryKey>;
  opt?: Omit<
    UseInfiniteQueryOptions<
      TPostInfinityQuery,
      Error,
      TPostInfinityQuery,
      TPostInfinityQuery,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >;
  groupMode?: boolean;
  emptyScreen?: React.ReactElement;
}
interface IPostFeedWithoutInfinityQuery {
  queryKey: QueryKey;
  queryFn: QueryFunction<TPostQuery, QueryKey>;
  opt?: Omit<
    UseQueryOptions<TPostQuery, Error, TPostQuery, QueryKey>,
    'queryKey' | 'queryFn'
  >;
  emptyScreen?: React.ReactElement;
}

export const ListSkeleton = () => {
  return (
    <>
      {new Array(2).fill(0).map((_value, index) => (
        <PostSkeleton key={index} />
      ))}
    </>
  );
};

const PostFeedWithInfinityQuery = ({
  queryFn,
  queryKey,
  opt,
  groupMode = false,
  emptyScreen,
}: IPostFeedWithInfinityQuery) => {
  const {
    postItems,
    queryResult: {
      isLoading,
      isFetching,
      isFetchingNextPage,
      fetchNextPage,
      data,
    },
  } = usePostManagementWithInfinityQuery({
    queryFn,
    queryKey,
    opt,
  });

  const { ref, inView } = useInView(); //detect if element in the viewport
  const meta = (data as any)?.meta;

  useEffect(() => {
    if (inView && !isFetchingNextPage && postItems.length < meta?.totalCount)
      fetchNextPage();
  }, [fetchNextPage, inView, isFetchingNextPage, postItems, meta]);

  return (
    <>
      <div className="mx-auto w-full max-w-[524px] xl:w-[524px]">
        {isLoading || (isFetching && !isFetchingNextPage) ? (
          <ListSkeleton />
        ) : postItems.length > 0 ? (
          postItems.map((_item, _index) => (
            <>
              <Post key={_item.postId} {..._item} groupMode={groupMode} />
              {_index === postItems.length - 3 && (
                <div
                  ref={ref}
                  className="invisible h-[1px] w-full bg-transparent"
                ></div>
              )}
            </>
          ))
        ) : (
          emptyScreen ?? null
        )}
      </div>
      {isFetchingNextPage && <ListSkeleton />}
    </>
  );
};

export const PostFeedWithoutInfinityQuery = ({
  queryFn,
  queryKey,
  opt,
  emptyScreen,
}: IPostFeedWithoutInfinityQuery) => {
  const {
    canContinueFetch,
    postItems,
    queryResult: { isLoading, isFetching, refetch },
  } = usePostManagementWithoutInfinityQuery({ queryFn, queryKey, opt });

  const { ref, inView } = useInView(); //detect if element in the viewport

  useEffect(() => {
    if (inView && !isFetching && canContinueFetch) {
      refetch();
    }
  }, [canContinueFetch, inView, isFetching, refetch]);

  return (
    <>
      <div className=" mx-auto w-full max-w-[524px] xl:w-[524px]">
        {isLoading ? (
          <ListSkeleton />
        ) : postItems.length > 0 ? (
          postItems.map((_item) => <Post key={_item.postId} {..._item} />)
        ) : (
          emptyScreen ?? null
        )}
      </div>

      <div className="mx-auto w-full max-w-[524px] xl:w-[524px]">
        {isFetching && <ListSkeleton />}
      </div>

      {!isFetching && postItems.length > 0 && (
        <div
          ref={ref}
          className="invisible h-[1px] w-full bg-transparent"
        ></div>
      )}
    </>
  );
};

export default PostFeedWithInfinityQuery;
